import React, { useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, LogIn } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { setUserToken } from "@/utils/userAuth";
import { setPixelUserIdentity } from "@/utils/pixel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LoginPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      const { data } = await axios.post(`${API}/users/login`, {
        email: form.email.trim(),
        password: form.password ? form.password : undefined,
      });
      setUserToken(data.access_token, true);
      // Propagate user identity to Meta Pixel so subsequent events include
      // `external_id` for Advanced Matching (mirrors server-side CAPI).
      try { if (data?.user?.id) setPixelUserIdentity(data.user.id); } catch { /* ignore */ }
      setStatus("success");
      setTimeout(() => navigate(data.redirect_to || "/corso/area-membri"), 250);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Impossibile accedere.";
      setError(typeof detail === "string" ? detail : "Email o password non corretti.");
      setStatus("error");
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center px-6 py-12" style={{ background: "var(--np-tertiary)" }} data-testid="login-page">
      <div className="w-full max-w-[460px] bg-white rounded-2xl shadow-[0_30px_80px_-30px_rgba(26,42,58,0.18)] overflow-hidden border" style={{ borderColor: "rgba(26,42,58,0.06)" }}>
        <div className="px-7 pt-7 pb-2 text-center" style={{ background: "var(--np-quaternary)" }}>
          <Link to="/" aria-label="Neuroscienza Pratica" className="inline-flex items-center justify-center">
            <img
              src="https://customer-assets.emergentagent.com/job_ciclo-responsabilita/artifacts/ypgcjh4z_11%20%2829%29.png"
              alt="Neuroscienza Pratica"
              className="block mx-auto"
              style={{ width: 260, aspectRatio: "4 / 1", objectFit: "cover", objectPosition: "center" }}
            />
          </Link>
        </div>

        <div className="px-7 py-7">
          <h1 className="text-2xl text-center" style={{ fontFamily: "Merriweather, serif", color: "var(--np-primary)" }}>
            Accedi al tuo corso
          </h1>
          <p className="mt-2 text-sm text-center" style={{ color: "var(--np-muted)" }}>
            Inserisci le credenziali create al momento dell&apos;acquisto.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="login-email" className="text-[13px] font-semibold tracking-wide uppercase" style={{ color: "var(--np-primary)" }}>Email</Label>
              <Input id="login-email" data-testid="login-email" type="email" required autoComplete="email"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="tuo@email.it"
                className="h-11 rounded-lg border-[#dbe3ec] focus-visible:ring-[var(--np-secondary)]" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="login-password" className="text-[13px] font-semibold tracking-wide uppercase" style={{ color: "var(--np-primary)" }}>Password</Label>
              <Input id="login-password" data-testid="login-password" type="password" autoComplete="current-password"
                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="La tua password"
                className="h-11 rounded-lg border-[#dbe3ec] focus-visible:ring-[var(--np-secondary)]" />
              <p className="text-[12px] mt-1" style={{ color: "var(--np-muted)" }}>
                Hai un vecchio account senza password? Lascia il campo vuoto e accedi solo con email.
              </p>
            </div>

            {status === "error" && (
              <p data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>
            )}

            <button type="submit" data-testid="login-submit" disabled={status === "loading"}
              className="cta-btn w-full mt-2 disabled:opacity-60 disabled:cursor-not-allowed">
              {status === "loading" ? (<><Loader2 className="animate-spin" size={18} /> Sto accedendo…</>)
                : status === "success" ? (<>Reindirizzo…</>)
                : (<><LogIn size={18} /> Accedi</>)}
            </button>
          </form>

          <p className="mt-7 text-center text-sm" style={{ color: "var(--np-muted)" }}>
            Non hai ancora un account?{" "}
            <Link to="/" className="underline-offset-2 hover:underline font-semibold" style={{ color: "var(--np-secondary)" }}>
              Inizia con la lezione gratuita
            </Link>
          </p>
        </div>
      </div>
    </main>
  );
}
